'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

const countries = [
  { code: 'DE', name: 'Niemcy', rate: '19%' },
  { code: 'FR', name: 'Francja', rate: '20%' },
  { code: 'IT', name: 'Włochy', rate: '22%' },
  { code: 'ES', name: 'Hiszpania', rate: '21%' },
  { code: 'NL', name: 'Holandia', rate: '21%' },
  { code: 'PL', name: 'Polska', rate: '23%' },
];

interface SpecjalizacjaContent {
  title: string;
  subtitle: string;
}

export default function SpecjalizacjaSection() {
  const [content, setContent] = useState<SpecjalizacjaContent>({
    title: 'Faktury OSS i Amazon per kraj.',
    subtitle: 'Sprzedaż cross-border przez Amazon EU wymaga wystawiania faktur z prawidłową stawką VAT dla każdego kraju odbiorcy.',
  });
  const supabase = createClient();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['specjalizacja_title', 'specjalizacja_subtitle']);

        if (error) {
          console.error('Error fetching specjalizacja content:', error.message);
          return;
        }

        const settingsMap: Record<string, string> = {};
        data?.forEach((setting) => {
          settingsMap[setting.key] = setting.value;
        });

        setContent({
          title: settingsMap['specjalizacja_title'] || content.title,
          subtitle: settingsMap['specjalizacja_subtitle'] || content.subtitle,
        });
      } catch (err) {
        console.error('Error:', err);
      }
    };

    fetchContent();
  }, [supabase]);

  return (
    <section className="py-20 md:py-28 bg-offwhite">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left content */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-3">
              <p className="section-label">Specjalizacja</p>
              <h2 className="text-4xl md:text-5xl font-900 tracking-tight text-navy leading-[0.95]">
                {content.title}
              </h2>
            </div>
            <p className="text-base text-muted font-500 leading-relaxed">
              {content.subtitle}
            </p>

            <div className="space-y-4">
              {[
                {
                  icon: 'CheckCircleIcon' as const,
                  text: 'Automatyczne wykrywanie kraju odbiorcy z zamówienia Amazon',
                },
                {
                  icon: 'CheckCircleIcon' as const,
                  text: 'Mapowanie stawek VAT per kraj UE zgodnie z OSS',
                },
                {
                  icon: 'CheckCircleIcon' as const,
                  text: 'Integracja z wFirma, Fakturownia lub InFakt',
                },
                {
                  icon: 'CheckCircleIcon' as const,
                  text: 'Raportowanie OSS — dane gotowe do deklaracji',
                },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <Icon name={item.icon} size={18} className="text-blue-DEFAULT mt-0.5 flex-shrink-0" variant="solid" />
                  <span className="text-sm font-600 text-navy/80 leading-snug">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Diagram */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-2xl border border-border p-8 space-y-6">
              <p className="text-xs font-800 uppercase tracking-widest text-muted">
                Diagram procesu — Fakturowanie OSS
              </p>

              {/* Flow diagram */}
              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-center gap-3">
                  <div className="diagram-node active flex-1 text-center">
                    Zamówienie Amazon EU
                  </div>
                </div>
                <div className="flex justify-center">
                  <Icon name="ArrowDownIcon" size={16} className="text-muted" variant="outline" />
                </div>

                {/* Step 2 */}
                <div className="diagram-node active text-center">
                  BaseLinker / Apilo — reguła automatyczna
                </div>
                <div className="flex justify-center">
                  <Icon name="ArrowDownIcon" size={16} className="text-muted" variant="outline" />
                </div>

                {/* Step 3: country split */}
                <div className="grid grid-cols-3 gap-2">
                  {countries.map((c) => (
                    <div
                      key={c.code}
                      className="bg-blue-pale border border-blue-DEFAULT/20 rounded-xl p-3 text-center"
                    >
                      <div className="text-sm font-800 text-navy">{c.name}</div>
                      <div className="text-xs font-700 text-blue-DEFAULT mt-1">
                        VAT {c.rate}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center">
                  <Icon name="ArrowDownIcon" size={16} className="text-muted" variant="outline" />
                </div>

                {/* Step 4 */}
                <div className="diagram-node highlight text-center">
                  Faktura wystawiona automatycznie → system faktur
                </div>
              </div>

              <div className="pt-4 border-t border-border flex items-center gap-2">
                <Icon name="ClockIcon" size={14} className="text-muted" variant="outline" />
                <span className="text-xs font-600 text-muted">
                  Czas wystawienia faktury: &lt;3 sekundy od złożenia zamówienia
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}